import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Package, Compass, Megaphone, Pause, Play, ImagePlus, Trash2, Pencil, X, Send, ExternalLink, Rocket } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { ListingComposer } from "@/components/ListingComposer";

const UPDATE_KINDS = [
  { id: "news", label: "News" }, { id: "new_arrival", label: "New Arrival" },
  { id: "hunt_report", label: "Hunt Report" }, { id: "event", label: "Event" }, { id: "sale", label: "Sale" },
];
const KIND_LABEL = Object.fromEntries(UPDATE_KINDS.map((k) => [k.id, k.label]));
const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "hunts", label: "Hunts", icon: Compass },
  { id: "updates", label: "Updates", icon: Megaphone },
];

export default function PartnerPublish() {
  const { isAuthed, ready } = useAuth();
  const { setAuthOpen } = useUI();
  const navigate = useNavigate();
  const [status, setStatus] = useState(undefined);
  const [tab, setTab] = useState("products");
  const [biz, setBiz] = useState(null);
  const [listings, setListings] = useState([]);

  const loadListings = useCallback(() => {
    api.myBusiness().then((d) => { setBiz(d.business); setListings((d.listings || []).filter((l) => l.status !== "deleted")); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ready || !isAuthed) return;
    api.partnerOnboarding().then((ob) => { setStatus(ob); if (ob.onboarding_complete) loadListings(); }).catch(() => setStatus(null));
  }, [ready, isAuthed, loadListings]);

  if (!ready || (isAuthed && status === undefined)) return <Shell><Center><Loader2 className="animate-spin text-forest" /></Center></Shell>;
  if (!isAuthed) return (
    <Shell><Center><div className="text-center" data-testid="publish-signin-gate">
      <h1 className="font-serif text-3xl text-forest">Publish to HomeRange</h1>
      <button onClick={() => setAuthOpen(true)} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full font-semibold" data-testid="publish-signin-btn">Sign in</button>
    </div></Center></Shell>
  );
  if (!status?.onboarding_complete) return (
    <Shell><Center><div className="text-center max-w-md" data-testid="publish-gate">
      <Rocket size={30} className="mx-auto text-blaze" />
      <h1 className="font-serif text-3xl text-forest mt-4">Finish your profile to start publishing</h1>
      <p className="text-charcoal/60 mt-2">
        {status?.step === "subscribe" ? "Activate a subscription to unlock publishing." : "Complete and publish your partner profile first."}
      </p>
      <button onClick={() => navigate("/partner/onboarding")} className="mt-6 bg-forest text-bone px-6 py-3 rounded-full font-semibold hover:bg-forest-2 transition-colors" data-testid="publish-go-onboarding">Continue onboarding</button>
    </div></Center></Shell>
  );

  const products = listings.filter((l) => l.listing_type === "product");
  const hunts = listings.filter((l) => l.listing_type === "hunt" || l.listing_type === "service");

  return (
    <Shell>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Publishing</span>
            <h1 className="font-serif text-3xl text-forest mt-1">Publish to HomeRange</h1>
            <p className="text-sm text-charcoal/60 mt-1">Add products, guided hunts and updates — they appear across HomeRange and on your profile.</p>
          </div>
          <button onClick={() => navigate("/partner/dashboard")} className="inline-flex items-center gap-1.5 border border-saddle/25 bg-white px-4 py-2.5 rounded-full text-sm font-semibold text-charcoal/80 hover:border-forest transition-colors" data-testid="publish-dashboard-link"><ExternalLink size={14} /> Dashboard</button>
        </div>

        <div className="flex gap-2 mt-6 border-b border-saddle/15" data-testid="publish-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-blaze text-forest" : "border-transparent text-charcoal/50 hover:text-forest"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "products" && (
            <div data-testid="panel-products">
              <ListingComposer lockType="product" onPublished={loadListings} />
              <ListingList title="Your products" items={products} onChanged={loadListings} emptyLabel="No products yet — add your first above." />
            </div>
          )}
          {tab === "hunts" && (
            <div data-testid="panel-hunts">
              <ListingComposer lockType="hunt" onPublished={loadListings} />
              <ListingList title="Your hunts" items={hunts} onChanged={loadListings} emptyLabel="No hunts listed yet — add your first above." />
            </div>
          )}
          {tab === "updates" && <UpdatesPanel partner={status.partner} />}
        </div>
      </div>
    </Shell>
  );
}

function ListingList({ title, items, onChanged, emptyLabel }) {
  const toggle = async (l) => {
    const next = l.status === "active" ? "paused" : "active";
    try { await api.setListingStatus(l.id, next); toast.success(next === "active" ? "Live" : "Paused"); onChanged(); }
    catch { toast.error("Could not update"); }
  };
  return (
    <div className="mt-6">
      <h3 className="font-serif text-lg text-forest mb-3">{title} ({items.length})</h3>
      {items.length === 0 ? <p className="text-sm text-charcoal/50 border border-dashed border-saddle/30 bg-white rounded-lg p-6 text-center">{emptyLabel}</p> : (
        <div className="space-y-2" data-testid="publish-listing-list">
          {items.map((l) => (
            <div key={l.id} className="flex items-center gap-3 bg-white border border-saddle/20 rounded-lg p-3" data-testid={`publish-listing-${l.id}`}>
              {l.images?.[0] ? <img src={l.images[0]} alt="" className="w-12 h-12 object-cover rounded shrink-0" /> : <div className="w-12 h-12 rounded bg-paper grid place-items-center shrink-0"><Package size={16} className="text-saddle/50" /></div>}
              <div className="flex-1 min-w-0">
                <strong className="text-forest block truncate">{l.title}</strong>
                <span className="text-xs text-charcoal/50">${l.price}{l.species ? ` · ${l.species}` : ""}{l.status === "paused" ? " · PAUSED" : ""}</span>
              </div>
              <button onClick={() => toggle(l)} className="text-saddle hover:text-forest p-1.5" aria-label="Toggle" data-testid={`publish-toggle-${l.id}`}>{l.status === "active" ? <Pause size={16} /> : <Play size={16} />}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UpdatesPanel({ partner }) {
  const [updates, setUpdates] = useState(null);
  const [editing, setEditing] = useState(null); // update being edited, or "new"
  const load = useCallback(() => { api.partnerMyUpdates({ limit: 20 }).then((d) => setUpdates(d.updates)).catch(() => setUpdates([])); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await api.partnerDeleteUpdate(id); toast.success("Update deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="panel-updates">
      {editing ? (
        <UpdateComposer update={editing === "new" ? null : editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <button onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 bg-forest text-bone px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-forest-2 transition-colors" data-testid="new-update-btn">
          <Megaphone size={15} /> Post an update
        </button>
      )}
      <p className="text-xs text-charcoal/50 mt-2">Updates appear on your profile and in the HomeRange community feed.</p>

      {updates === null ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        : updates.length === 0 ? <p className="text-sm text-charcoal/50 border border-dashed border-saddle/30 bg-white rounded-lg p-6 text-center mt-4">No updates yet.</p>
        : (
          <div className="space-y-3 mt-5" data-testid="updates-list">
            {updates.map((u) => (
              <div key={u.id} className="bg-white border border-saddle/20 rounded-lg p-4 flex gap-3" data-testid={`update-${u.id}`}>
                {u.media && <img src={u.media} alt="" className="w-16 h-16 object-cover rounded shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase bg-blaze/10 text-blaze px-2 py-0.5 rounded-full">{KIND_LABEL[u.post_kind] || "News"}</span>
                    {u.hidden && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid={`update-hidden-${u.id}`}>Hidden by admin</span>}
                  </div>
                  {u.title && <strong className="text-forest block mt-1">{u.title}</strong>}
                  {u.text && <p className="text-sm text-charcoal/70 mt-0.5 line-clamp-2">{u.text}</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => setEditing(u)} className="text-charcoal/50 hover:text-forest p-1.5" aria-label="Edit" data-testid={`update-edit-${u.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(u.id)} className="text-charcoal/50 hover:text-red-600 p-1.5" aria-label="Delete" data-testid={`update-delete-${u.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function UpdateComposer({ update, onDone, onCancel }) {
  const [form, setForm] = useState({ title: update?.title || "", body: update?.text || "", kind: update?.post_kind || "news", image: update?.media || "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const res = await api.partnerUploadUpdateImage(file); setForm((f) => ({ ...f, image: res.url })); }
    catch { toast.error("Image upload failed"); }
    setUploading(false);
  };
  const submit = async () => {
    if (!form.title.trim() && !form.body.trim()) { toast.error("Add a title or some text"); return; }
    setBusy(true);
    try {
      if (update) await api.partnerEditUpdate(update.id, form);
      else await api.partnerCreateUpdate(form);
      toast.success(update ? "Update saved" : "Update published");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-saddle/20 rounded-xl p-5" data-testid="update-composer">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg text-forest">{update ? "Edit update" : "New update"}</h3>
        <button onClick={onCancel} className="text-charcoal/40 hover:text-forest" aria-label="Close" data-testid="update-cancel"><X size={18} /></button>
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {UPDATE_KINDS.map((k) => (
          <button key={k.id} onClick={() => setForm((f) => ({ ...f, kind: k.id }))}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${form.kind === k.id ? "bg-forest text-bone border-forest" : "border-saddle/25 text-charcoal/60 hover:border-forest"}`}
            data-testid={`update-kind-${k.id}`}>{k.label}</button>
        ))}
      </div>
      <input value={form.title} onChange={set("title")} placeholder="Title (e.g. New elk packages for 2026)" className="w-full border border-saddle/25 px-3 py-2.5 rounded-lg text-charcoal font-semibold" data-testid="update-title" />
      <textarea value={form.body} onChange={set("body")} rows={4} placeholder="Share the news…" className="w-full border border-saddle/25 px-3 py-2.5 rounded-lg text-charcoal mt-2" data-testid="update-body" />
      <div className="flex items-center gap-3 mt-3">
        <label className="w-20 h-20 shrink-0 rounded-lg border border-dashed border-saddle/40 overflow-hidden grid place-items-center cursor-pointer hover:border-forest" data-testid="update-image-upload">
          {form.image ? <img src={form.image} alt="" className="w-full h-full object-cover" /> : (uploading ? <Loader2 size={18} className="animate-spin text-saddle" /> : <ImagePlus size={18} className="text-saddle" />)}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {form.image && <button onClick={() => setForm((f) => ({ ...f, image: "" }))} className="text-xs text-charcoal/50 hover:text-red-600" data-testid="update-image-remove">Remove image</button>}
      </div>
      <button onClick={submit} disabled={busy} className="mt-4 inline-flex items-center gap-1.5 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50 hover:bg-blaze-dark transition-colors" data-testid="update-submit">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {update ? "Save changes" : "Publish update"}
      </button>
    </div>
  );
}

function Center({ children }) { return <div className="py-32 grid place-items-center">{children}</div>; }
function Shell({ children }) {
  return (
    <div className="bg-paper min-h-screen" data-testid="partner-publish">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">{children}</main>
      <Footer />
    </div>
  );
}
